import { Box, MenuItem, Select, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import Translations from 'src/@core/layouts/components/Translations'
import { Land, OperationUnit } from 'src/@types'
import { AppDispatch, RootState } from 'src/store'
import { fetchOneLand } from 'src/store/slices/land-slice'
import { fetchRainCardDashboard } from 'src/store/slices/rainSlice'
import { fetchRodeosCardInfo } from 'src/store/slices/rodeoSlice'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Ruler, LandPlots, AccountOutline } from 'mdi-material-ui'
import { fetchDashboardData } from 'src/store/slices/dashboard-slice'
import { useSelector } from 'react-redux'
import { numberFormatAsked } from 'src/@core/utils/number-format-asked'

interface DashBoardNavProps {
  operationUnits: OperationUnit[]
  land?: Land
}

export const DashboardNav = ({ operationUnits}: DashBoardNavProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const [selectedUnit, setSelectedUnit] = useState<OperationUnit | null>(null)
  const [refreshInput, setRefreshInput] = useState<boolean>(false)
  const [selectedLand, setSelectedLand] = useState<Land | null>(null)
  const [landsOptions, setLandsOptions] = useState<Land[]>([])
  const { dashboard } = useSelector((state: RootState) => state.dashboard)

  useEffect(() => {
    dispatch(fetchDashboardData())
  }, [dispatch])

  useEffect(() => {
    setRefreshInput(true)
    if (selectedUnit) {
      setLandsOptions(selectedUnit.lands || [])
    }
  }, [selectedUnit])

  useEffect(() => {
    if (selectedLand) {
      dispatch(fetchOneLand(selectedLand.id!))
      dispatch(
        fetchRainCardDashboard({
          landId: selectedLand.id!
        })
      )
      dispatch(fetchRodeosCardInfo(selectedLand.id!))
    }
  }, [selectedLand])

  useEffect(() => {
    if (operationUnits?.length) {
      const operationUnitWithData = operationUnits.find(operation => operation.lands?.length)
      const land = operationUnitWithData?.lands?.[0]
      setSelectedUnit(operationUnitWithData ?? null)
      setSelectedLand(land ?? null)
    }
  }, [operationUnits])

  const handleChangeOfOperationUnit = (e: any) => {
    setRefreshInput(false)
    const selectedId = e.target.value as number
    const operationUnit = operationUnits.find(unit => unit.id === selectedId)
    setSelectedUnit(operationUnit || null)
    if (operationUnit && operationUnit.lands && operationUnit.lands.length) {
      setSelectedLand(operationUnit.lands[0])
    }
  }

  const handleChangeOfLand = (e: any) => {
    const selectedId = e.target.value as number
    setSelectedLand(landsOptions.find(land => land.id === selectedId) || null)
  }

  return (
    <Box
      sx={{
        borderBottom: 3,
        borderColor: '#D9D9D9',
        width: '100%'
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Typography sx={{ color: 'black', fontSize: 'clamp(20px, 30px, 40px)', fontWeight: '700' }}>
          <Translations text={'Dashboard'} />
        </Typography>
        {operationUnits.length ? (
          <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
            <Select
              onChange={handleChangeOfOperationUnit}
              sx={{
                mr: 5,
                padding: 0,
                '& .MuiSelect-select': {
                  color: 'black',
                  bgcolor: 'white',
                  fontSize: 'clamp(10px, 15px, 20px)',
                  fontWeight: '700'
                },
                '& .MuiSvgIcon-root': {
                  fill: 'black'
                },
                position: 'relative'
              }}
              variant='standard'
              defaultValue={operationUnits[0].id}
              IconComponent={KeyboardArrowDownIcon}
              style={{ color: 'black' }}
              disableUnderline
            >
              {operationUnits.map(unit => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.name}
                </MenuItem>
              ))}
            </Select>
            {selectedUnit?.lands?.length && refreshInput ? (
              <Select
                onChange={handleChangeOfLand}
                sx={{
                  '& .MuiSelect-select': {
                    color: 'black',
                    bgcolor: 'white',
                    fontSize: 'clamp(10px, 15px, 20px)',
                    fontWeight: '700'
                  },
                  '& .MuiSvgIcon-root': {
                    fill: 'black'
                  },
                  position: 'relative'
                }}
                variant='standard'
                defaultValue={selectedUnit.lands[0].id}
                IconComponent={KeyboardArrowDownIcon}
                style={{ color: 'black' }}
                disableUnderline
              >
                {selectedUnit.lands.map(land => (
                  <MenuItem key={land.id} value={land.id}>
                    {land.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              ''
            )}
          </Box>
        ) : null}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          flexWrap: 'wrap',
          alignItems: 'center',
          borderTop: '3px solid #D9D9D9',
          padding: 2
        }}
      >
        {dashboard?.operationsUnits && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <LandPlots />
              <Typography sx={{ fontSize: '18px', fontWeight: 400, ml: '10px', mr: '10px' }}>
                <Translations text={'Operation units: '} />
              </Typography>
              <Typography sx={{ fontSize: '24px', fontWeight: 500 }}>
                {numberFormatAsked(dashboard.operationsUnits) || null}
              </Typography>
            </Box>
          </>
        )}
        {dashboard?.totalUsers && (
          <>
            <Box sx={{ width: '1px', height: '30px', backgroundColor: '#000', ml: '30px', mr: '30px' }} />
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <AccountOutline />
              <Typography sx={{ fontSize: '18px', fontWeight: 400, ml: '10px', mr: '10px' }}>
                <Translations text={'Users: '} />
              </Typography>
              <Typography sx={{ fontSize: '24px', fontWeight: 500 }}>
                {numberFormatAsked(dashboard.totalUsers) || null}
              </Typography>
            </Box>
          </>
        )}

        {dashboard?.landsArea && (
          <>
            <Box sx={{ width: '1px', height: '30px', backgroundColor: '#000', ml: '30px', mr: '30px' }} />
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Ruler />
              <Typography sx={{ fontSize: '18px', fontWeight: 400, ml: '10px', mr: '10px' }}>
                <Translations text={'Total has: '} />
              </Typography>
              <Typography sx={{ fontSize: '24px', fontWeight: 500 }}>
                {numberFormatAsked(dashboard.landsArea) || null}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
